/**
 * CallDetail — one call, fully unpacked (§13/§14), at /calls/:id.
 *
 * Header (agent, direction, duration, status, end reason, engine, cost),
 * latency metrics strip, the voice ribbon rendered as a static timeline from
 * transcript turn timings (click/arrow-key to scrub — the playhead highlights
 * the turn under it), the transcript with per-turn latency + retrieved-chunk
 * expandos, and handoff system turns rendered as visible dividers.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { Link, useParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import {
  cx,
  formatCost,
  formatDateTime,
  formatDuration,
  formatMs,
  formatPhone,
  formatTurnOffset,
  shortId,
} from "../lib/format";
import type { AgentSummary, CallDetail as CallDetailData, TranscriptTurn } from "../lib/types";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { Spinner } from "../components/Spinner";
import { VoiceRibbon } from "../components/VoiceRibbon";
import {
  CallStatusCell,
  DirectionBadge,
  EngineBadge,
  endReasonLabel,
} from "../components/console/callDisplay";

/** "m:ss.mmm" — exact offset for the turn-latency table. */
function formatExactOffset(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "—";
  const clamped = Math.max(0, ms);
  const m = Math.floor(clamped / 60_000);
  const rest = (clamped % 60_000) / 1000;
  return `${m}:${rest.toFixed(3).padStart(6, "0")}`;
}

const RIBBON_SAMPLES = 280;
/** Rough speech pacing used when a turn has no recorded duration. */
const MS_PER_CHAR = 60;
const MIN_TURN_MS = 800;

interface TimedTurn {
  turn: TranscriptTurn;
  startMs: number;
  endMs: number;
}

function timedTurns(transcript: TranscriptTurn[]): TimedTurn[] {
  return transcript
    .filter((t) => t.role !== "system" && t.started_at_ms !== null)
    .map((t) => {
      const startMs = t.started_at_ms ?? 0;
      const durationMs = t.duration_ms ?? Math.max(MIN_TURN_MS, t.content.length * MS_PER_CHAR);
      return { turn: t, startMs, endMs: startMs + durationMs };
    })
    .sort((a, b) => a.startMs - b.startMs);
}

function buildRibbon(timed: TimedTurn[], totalMs: number) {
  const samples: number[] = new Array(RIBBON_SAMPLES).fill(0);
  const states: Array<"user" | "agent" | "silence"> = new Array(RIBBON_SAMPLES).fill("silence");
  if (totalMs <= 0) return { samples, states };
  timed.forEach((entry, turnIndex) => {
    const from = Math.max(0, Math.floor((entry.startMs / totalMs) * RIBBON_SAMPLES));
    const to = Math.min(RIBBON_SAMPLES, Math.ceil((entry.endMs / totalMs) * RIBBON_SAMPLES));
    for (let i = from; i < to; i++) {
      states[i] = entry.turn.role === "user" ? "user" : "agent";
      // Deterministic speech-like envelope — the stored transcript has no
      // audio samples, so the ribbon renders turn timing, not real amplitude.
      samples[i] = 0.28 + 0.5 * Math.abs(Math.sin(i * 0.9 + turnIndex * 2.7));
    }
  });
  return { samples, states };
}

/** Pull retrieved-chunk info out of turn metadata, whatever shape it took. */
function turnChunks(turn: TranscriptTurn): Array<{ id: string | null; content: string | null }> {
  const meta = turn.metadata;
  if (!meta) return [];
  const withContent = (meta as Record<string, unknown>).retrieved_chunks;
  if (Array.isArray(withContent)) {
    const mapped = withContent
      .map((c) => {
        if (c === null || typeof c !== "object") return null;
        const record = c as Record<string, unknown>;
        return {
          id: typeof record.id === "string" ? record.id : null,
          content: typeof record.content === "string" ? record.content : null,
        };
      })
      .filter((c): c is { id: string | null; content: string | null } => c !== null);
    if (mapped.length > 0) return mapped;
  }
  if (Array.isArray(meta.retrieved_chunk_ids)) {
    return meta.retrieved_chunk_ids
      .filter((id): id is string => typeof id === "string")
      .map((id) => ({ id, content: null }));
  }
  return [];
}

function isHandoffTurn(turn: TranscriptTurn): boolean {
  return turn.role === "system" && /transferred to/i.test(turn.content);
}

// ---------------------------------------------------------------------------
// Per-turn chunk expando
// ---------------------------------------------------------------------------

function ChunkExpando({ chunks }: { chunks: Array<{ id: string | null; content: string | null }> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-xs text-muted transition-colors hover:text-bright"
      >
        {open ? "▾" : "▸"} {chunks.length} knowledge {chunks.length === 1 ? "chunk" : "chunks"}
      </button>
      {open && (
        <ul className="mt-1.5 flex flex-col gap-1.5">
          {chunks.map((chunk, i) => (
            <li key={chunk.id ?? i} className="rounded border border-line bg-ink px-2.5 py-2">
              <span className="font-mono text-xs text-muted">
                {chunk.id ? shortId(chunk.id) : `#${i + 1}`}
              </span>
              {chunk.content ? (
                <p className="mt-1 whitespace-pre-wrap font-mono text-xs leading-4 text-body">
                  {chunk.content}
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted">
                  Chunk ID recorded; content lives in the agent's knowledge documents.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CallDetail() {
  const { id: callId } = useParams<{ id: string }>();

  const [call, setCall] = useState<CallDetailData | null>(null);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [activeTurnId, setActiveTurnId] = useState<number | null>(null);
  const [downloadingLogs, setDownloadingLogs] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  async function handleDownloadLogs() {
    if (!call || downloadingLogs) return;
    setDownloadingLogs(true);
    setLogsError(null);
    try {
      const blob = await api.calls.downloadLogs(call.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `call-${call.id}.log`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setLogsError(err instanceof ApiError ? err.message : "Could not download logs.");
    } finally {
      setDownloadingLogs(false);
    }
  }
  const turnRefs = useRef(new Map<number, HTMLLIElement>());

  useEffect(() => {
    if (!callId) return;
    const controller = new AbortController();
    api.calls
      .get(callId, controller.signal)
      .then(setCall)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Could not load this call.");
      });
    api.agents
      .list(controller.signal)
      .then(setAgents)
      .catch(() => undefined);
    return () => controller.abort();
  }, [callId]);

  const agentNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agents) map.set(agent.id, agent.name);
    return map;
  }, [agents]);

  const timed = useMemo(() => (call ? timedTurns(call.turns) : []), [call]);
  const totalMs = useMemo(() => {
    const fromDuration = call?.duration_seconds != null ? call.duration_seconds * 1000 : 0;
    const fromTurns = timed.reduce((max, t) => Math.max(max, t.endMs), 0);
    return Math.max(fromDuration, fromTurns, 1000);
  }, [call, timed]);
  const ribbon = useMemo(() => buildRibbon(timed, totalMs), [timed, totalMs]);

  function seekTo(u: number): void {
    const clamped = Math.min(1, Math.max(0, u));
    setProgress(clamped);
    const at = clamped * totalMs;
    let hit: TimedTurn | null = null;
    for (const entry of timed) {
      if (entry.startMs <= at) hit = entry;
      else break;
    }
    if (hit) {
      setActiveTurnId(hit.turn.id);
      turnRefs.current.get(hit.turn.id)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }

  function handleRibbonClick(event: MouseEvent<HTMLDivElement>): void {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    seekTo((event.clientX - rect.left) / rect.width);
  }

  function handleRibbonKey(event: KeyboardEvent<HTMLDivElement>): void {
    const current = progress ?? 0;
    if (event.key === "ArrowRight") seekTo(current + 0.02);
    else if (event.key === "ArrowLeft") seekTo(current - 0.02);
    else if (event.key === "Home") seekTo(0);
    else if (event.key === "End") seekTo(1);
    else return;
    event.preventDefault();
  }

  if (error) {
    return (
      <div className="flex flex-col gap-3">
        <h1 className="font-display text-xl tracking-display text-bright">Call detail</h1>
        <p className="text-sm text-danger">{error}</p>
        <Link to="/calls" className="text-sm text-muted underline hover:text-bright">
          Back to calls
        </Link>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  const agentName = agentNames.get(call.agent_id) ?? "Agent";
  const metrics = call.metrics ?? {};

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <header className="flex flex-col gap-2">
        <Link to="/calls" className="text-xs text-muted transition-colors hover:text-bright">
          ← Calls
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-xl tracking-display text-bright">
            Call <span className="font-mono text-lg text-body">{shortId(call.id)}</span>
          </h1>
          <DirectionBadge direction={call.direction} />
          <EngineBadge engine={metrics.engine} />
          <CallStatusCell status={call.status} />
          <div className="ml-auto flex items-center gap-2">
            {logsError && <span className="text-xs text-danger">{logsError}</span>}
            <Button size="sm" loading={downloadingLogs} onClick={handleDownloadLogs}>
              Download logs
            </Button>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3 lg:grid-cols-6">
          <div>
            <dt className="text-xs text-muted">Agent</dt>
            <dd>
              <Link
                to={`/agents/${call.agent_id}`}
                className="text-body transition-colors hover:text-bright"
              >
                {agentName}
              </Link>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Started</dt>
            <dd className="font-mono text-xs text-body">{formatDateTime(call.started_at)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Duration</dt>
            <dd className="font-mono text-xs text-body">{formatDuration(call.duration_seconds)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">End reason</dt>
            <dd className="text-xs text-body">{endReasonLabel(call.end_reason)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">From → to</dt>
            <dd className="font-mono text-xs text-body">
              {call.direction === "browser_test"
                ? "browser"
                : `${formatPhone(call.from_number)} → ${formatPhone(call.to_number)}`}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Estimated cost</dt>
            <dd className="font-mono text-xs text-body">{formatCost(call.cost_estimate)}</dd>
          </div>
        </dl>
      </header>

      {/* Latency metrics strip */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { label: "Avg voice-to-voice", value: formatMs(metrics.voice_to_voice?.avg_ms) },
          { label: "P95 voice-to-voice", value: formatMs(metrics.voice_to_voice?.p95_ms) },
          {
            // First audible response (an instant-ack clip on filler turns) —
            // the number the caller actually feels (ADR 0018).
            label: "First sound (avg)",
            value: formatMs(metrics.stages?.first_sound?.avg_ms),
          },
          {
            label: "Felt (est.)",
            value: formatMs(metrics.felt_estimate_ms),
          },
          {
            label: "Model reply (avg)",
            value: formatMs(metrics.stages?.server_reply?.avg_ms),
          },
          {
            label: "Interruptions",
            value: metrics.interruptions != null ? String(metrics.interruptions) : "—",
          },
        ].map(({ label, value }) => (
          <div key={label} className="rounded border border-line bg-surface px-3 py-2.5">
            <p className="text-xs text-muted">{label}</p>
            <p className="mt-0.5 font-mono text-lg text-bright">{value}</p>
          </div>
        ))}
      </div>

      {/* Timeline ribbon — static mode, scrub with click / arrow keys */}
      <section className="rounded border border-line bg-surface px-4 py-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted">Timeline</h2>
          <p className="font-mono text-xs text-muted">
            {progress !== null ? formatTurnOffset(progress * totalMs) : formatDuration(totalMs / 1000)}
          </p>
        </div>
        <div
          role="slider"
          tabIndex={0}
          aria-label="Call timeline — click or use arrow keys to scrub"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round((progress ?? 0) * 100)}
          onClick={handleRibbonClick}
          onKeyDown={handleRibbonKey}
          className="mt-2 cursor-pointer"
        >
          <VoiceRibbon
            samples={ribbon.samples}
            sampleStates={ribbon.states}
            progress={progress ?? undefined}
            state="idle"
            height={72}
          />
        </div>
        <p className="mt-1.5 text-xs text-muted">
          Bright is the caller, grey is the agent — rendered from turn timings.
        </p>
      </section>

      {call.summary && (
        <section className="rounded border border-line bg-surface px-4 py-3">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted">Summary</h2>
          <p className="mt-1.5 text-sm text-body">{call.summary}</p>
        </section>
      )}

      {/* Per-turn timeline — exact offsets from call start (pipeline clock).
          v2v = speech end → playback start; model = speech end → first audio
          byte from the model (the speech-to-speech round trip); output =
          first audio byte → playback start. */}
      {(metrics.turns?.length ?? 0) > 0 && (
        <section className="rounded border border-line bg-surface">
          <header className="border-b border-line px-4 py-2.5">
            <h2 className="text-xs font-medium uppercase tracking-wide text-muted">
              Turn latency
            </h2>
          </header>
          <div className="overflow-x-auto px-4 py-3">
            <table className="w-full min-w-[640px] text-left font-mono text-xs">
              <thead>
                <tr className="text-muted">
                  <th className="pb-1.5 pr-3 font-medium">#</th>
                  <th className="pb-1.5 pr-3 font-medium">Speech end</th>
                  <th className="pb-1.5 pr-3 font-medium">First audio</th>
                  <th className="pb-1.5 pr-3 font-medium">Speaking</th>
                  <th className="pb-1.5 pr-3 font-medium">V2V</th>
                  <th className="pb-1.5 pr-3 font-medium">First sound</th>
                  <th className="pb-1.5 pr-3 font-medium">Model</th>
                  <th className="pb-1.5 pr-3 font-medium">Output</th>
                  <th className="pb-1.5 font-medium" />
                </tr>
              </thead>
              <tbody className="text-body">
                {(metrics.turns ?? []).map((t) => (
                  <tr key={t.turn} className="border-t border-line/60">
                    <td className="py-1 pr-3 text-muted">{t.turn}</td>
                    <td className="py-1 pr-3">{formatExactOffset(t.t_user_stopped_ms)}</td>
                    <td className="py-1 pr-3">{formatExactOffset(t.t_first_audio_ms)}</td>
                    <td className="py-1 pr-3">{formatExactOffset(t.t_bot_speaking_ms)}</td>
                    <td className="py-1 pr-3 text-bright">{formatMs(t.v2v_ms)}</td>
                    <td className="py-1 pr-3">{formatMs(t.first_sound_ms)}</td>
                    <td className="py-1 pr-3">{formatMs(t.server_reply_ms)}</td>
                    <td className="py-1 pr-3">{formatMs(t.output_path_ms)}</td>
                    <td className="py-1">
                      {t.filler && <Badge mono>Ack</Badge>}
                      {t.orphaned && <Badge tone="warning">Orphaned</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Transcript */}
      <section className="rounded border border-line bg-surface">
        <header className="border-b border-line px-4 py-2.5">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted">Transcript</h2>
        </header>
        {call.turns.length === 0 ? (
          <p className="px-4 py-8 text-center text-xs text-muted">
            No transcript was recorded for this call.
          </p>
        ) : (
          <ol className="flex flex-col gap-3 px-4 py-3">
            {call.turns.map((turn) => {
              if (turn.role === "system") {
                const handoff = isHandoffTurn(turn);
                return (
                  <li
                    key={turn.id}
                    role="separator"
                    className="flex items-center gap-3 py-1.5"
                  >
                    <span aria-hidden="true" className="h-px flex-1 bg-line" />
                    <span
                      className={cx(
                        "flex items-center gap-1.5 text-xs",
                        handoff ? "text-bright" : "text-muted",
                      )}
                    >
                      {handoff && (
                        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
                          <path
                            d="M2 8h10m0 0-3-3m3 3-3 3"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                      {turn.content}
                    </span>
                    <span aria-hidden="true" className="h-px flex-1 bg-line" />
                  </li>
                );
              }

              const isUser = turn.role === "user";
              const chunks = turnChunks(turn);
              const latency = turn.metadata?.latency_ms;
              const active = activeTurnId === turn.id;
              return (
                <li
                  key={turn.id}
                  ref={(el) => {
                    if (el) turnRefs.current.set(turn.id, el);
                    else turnRefs.current.delete(turn.id);
                  }}
                  className={cx(
                    "-mx-2 flex flex-col gap-0.5 rounded px-2 py-1 transition-colors",
                    active && "bg-surface-raised",
                  )}
                >
                  <div className="flex items-baseline gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (turn.started_at_ms !== null && totalMs > 0) {
                          setProgress(turn.started_at_ms / totalMs);
                          setActiveTurnId(turn.id);
                        }
                      }}
                      className="font-mono text-xs text-muted transition-colors hover:text-bright"
                      aria-label={`Jump timeline to ${formatTurnOffset(turn.started_at_ms)}`}
                    >
                      {formatTurnOffset(turn.started_at_ms)}
                    </button>
                    <span
                      className={cx(
                        "text-xs font-medium uppercase tracking-wide",
                        isUser ? "text-bright" : "text-muted",
                      )}
                    >
                      {isUser ? "Caller" : "Agent"}
                    </span>
                    <span className="flex-1" />
                    {turn.metadata?.interrupted && <Badge tone="warning">Interrupted</Badge>}
                    {!isUser && latency != null && (
                      <span className="font-mono text-xs text-muted">
                        {formatMs(latency)}
                        {turn.metadata?.server_reply_ms != null &&
                          ` · model ${formatMs(turn.metadata.server_reply_ms)}`}
                        {turn.metadata?.output_path_ms != null &&
                          ` · out ${formatMs(turn.metadata.output_path_ms)}`}
                      </span>
                    )}
                  </div>
                  <p
                    className={cx(
                      "whitespace-pre-wrap font-mono text-xs leading-5",
                      isUser ? "text-bright" : "text-body",
                    )}
                  >
                    {turn.content}
                  </p>
                  {chunks.length > 0 && <ChunkExpando chunks={chunks} />}
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}
